'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart, isFeeLine, linkedProductIdOf, feeSideOf, variantHasSide } from '@/context/cart-context';
import { formatPrice } from '@/lib/shopify';
import type { CartItem } from '@/lib/shopify/types';
import { DISCOUNT_TIERS, isDiscountLadderEnabled, LADDER_AUTO_APPLY_ENABLED } from '@/lib/discount-ladder';

function subscribeToHostnameChange() {
  return () => {};
}

function moneyAmount(value: string | undefined): number {
  const amount = Number.parseFloat(value || '0');
  return Number.isFinite(amount) ? amount : 0;
}

function formatCurrency(amount: number, currencyCode = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
  }).format(Math.max(0, amount));
}

export function CartDrawer() {
  const { cart, isOpen, closeCart, updateItem, removeItem, applyCartDiscount, isLoading } = useCart();
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>({});
  const [discountMessage, setDiscountMessage] = useState('');
  // Two-step delete: first trash click arms confirm UI for that line; second
  // click ("Confirm") actually removes. Cancel reverts.
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const lastDiscountRequestRef = useRef('');

  const cartItems = cart?.lines || [];
  const subtotal = cart?.cost?.subtotalAmount;
  const totalQuantity = cart?.totalQuantity || 0;
  const discountLadderEnabled = useSyncExternalStore(
    subscribeToHostnameChange,
    isDiscountLadderEnabled,
    () => false
  );
  const subtotalAmount = moneyAmount(subtotal?.amount);
  const subtotalCurrency = subtotal?.currencyCode || 'GBP';
  const currentLadderCode = cart?.discountCodes.find((discount) =>
    DISCOUNT_TIERS.some((tier) => tier.code === discount.code)
  )?.code || '';
  const eligibleTier = DISCOUNT_TIERS
    .filter((tier) => subtotalAmount >= tier.threshold)
    .at(-1) || null;
  const nextTier = DISCOUNT_TIERS.find((tier) => subtotalAmount < tier.threshold) || null;
  const ladderGoal = nextTier || DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1];
  const ladderProgress = ladderGoal
    ? Math.min(100, (subtotalAmount / ladderGoal.threshold) * 100)
    : 100;
  const amountToNextTier = nextTier ? nextTier.threshold - subtotalAmount : 0;
  const estimatedSavings = eligibleTier ? subtotalAmount * (eligibleTier.percent / 100) : 0;
  const hasNonLadderDiscount = Boolean(cart?.discountCodes.some((discount) =>
    !DISCOUNT_TIERS.some((tier) => tier.code === discount.code)
  ));
  const visibleDiscountMessage = hasNonLadderDiscount
    ? 'A different discount code is already applied.'
    : discountMessage;

  useEffect(() => {
    if (!discountLadderEnabled || !cart || isLoading) return;
    // Preview mode: don't push codes to Shopify until they exist (avoids a
    // "code not active" error). The progress UI below still renders.
    if (!LADDER_AUTO_APPLY_ENABLED) return;
    if (hasNonLadderDiscount) return;

    const targetCode = eligibleTier?.code || '';
    if (targetCode === currentLadderCode) return;

    const requestKey = `${cart.id}:${subtotalAmount.toFixed(2)}:${currentLadderCode}:${targetCode}`;
    if (lastDiscountRequestRef.current === requestKey) return;
    lastDiscountRequestRef.current = requestKey;

    let cancelled = false;

    applyCartDiscount(targetCode || undefined).then((error) => {
      if (cancelled) return;
      setDiscountMessage(error || (targetCode ? `${targetCode} discount applied.` : 'Discount removed until you reach £150.'));
    });

    return () => {
      cancelled = true;
    };
  }, [
    applyCartDiscount,
    cart,
    currentLadderCode,
    discountLadderEnabled,
    eligibleTier,
    hasNonLadderDiscount,
    isLoading,
    subtotalAmount,
  ]);

  // Match each digitising fee against every garment of the same product that
  // selected the matching side. For example: cart has Tee A · L (front), M
  // (back), XL (front+back). The front fee shows under L (primary, with
  // price) and XL (secondary, "(included)" badge). The back fee shows under
  // M (primary) and XL (secondary). One £25 fee charged once, but the
  // customer sees coverage on every variant that benefits.
  //
  // Backward compat for legacy carts (pre side-flag): if no garment of the
  // linked product carries the side flag, fall back to the legacy
  // _linkedVariant association so old carts keep rendering correctly.
  type FeeAssoc = { fee: CartItem; isPrimary: boolean };
  const feeChildrenByGarmentId = new Map<string, FeeAssoc[]>();
  const topLevelLines: CartItem[] = [];
  const feeIsLockedById = new Map<string, boolean>();
  // Pre-pass: bucket primary/secondary associations for every fee. The primary
  // should be the variant that ORIGINALLY triggered the fee (recorded as
  // _linkedVariant at add-time) so the £ display doesn't jump to a newer
  // variant when one is added later. Other matching variants are secondary.
  for (const fee of cartItems) {
    if (!isFeeLine(fee)) continue;
    const productId = linkedProductIdOf(fee, cartItems);
    const side = feeSideOf(fee);
    let matches: CartItem[] = [];
    if (productId && side) {
      matches = cartItems.filter(
        (g) =>
          g.id !== fee.id &&
          !isFeeLine(g) &&
          g.merchandise.product.id === productId &&
          variantHasSide(g, side)
      );
      // Stable primary: hoist the originally-linked variant to position 0 if
      // it's still in the matches set. This keeps the fee anchored to the
      // first variant that triggered it, even when newer variants opt in.
      const linkedVariantId = fee.attributes.find((a) => a.key === '_linkedVariant')?.value;
      if (linkedVariantId && matches.length > 1) {
        const idx = matches.findIndex((m) => m.merchandise.id === linkedVariantId);
        if (idx > 0) {
          const [primary] = matches.splice(idx, 1);
          matches.unshift(primary);
        }
      }
    }
    if (matches.length === 0) {
      // Legacy cart fallback: locate via _linkedVariant first, else any
      // variant of the linked product.
      const linkedVariantId = fee.attributes.find((a) => a.key === '_linkedVariant')?.value;
      if (linkedVariantId) {
        const exact = cartItems.find(
          (g) => g.id !== fee.id && !isFeeLine(g) && g.merchandise.id === linkedVariantId
        );
        if (exact) matches = [exact];
      }
      if (matches.length === 0 && productId) {
        const productMatch = cartItems.find(
          (g) => g.id !== fee.id && !isFeeLine(g) && g.merchandise.product.id === productId
        );
        if (productMatch) matches = [productMatch];
      }
    }
    feeIsLockedById.set(fee.id, matches.length > 0);
    if (matches.length === 0) continue;
    matches.forEach((garment, idx) => {
      const list = feeChildrenByGarmentId.get(garment.id) ?? [];
      list.push({ fee, isPrimary: idx === 0 });
      feeChildrenByGarmentId.set(garment.id, list);
    });
  }
  // Top-level lines: every garment, plus any fee with no resolved parent
  // (orphan — kept rendering at top level so it isn't silently hidden).
  for (const line of cartItems) {
    if (isFeeLine(line)) {
      if (!feeIsLockedById.get(line.id)) topLevelLines.push(line);
      continue;
    }
    topLevelLines.push(line);
  }
  const findLinkedGarment = (feeLine: CartItem): CartItem | undefined => {
    for (const [garmentId, assocs] of feeChildrenByGarmentId) {
      if (assocs.some((a) => a.fee.id === feeLine.id && a.isPrimary)) {
        return cartItems.find((c) => c.id === garmentId);
      }
    }
    return undefined;
  };

  const handleUpdateQuantity = async (lineId: string, newQuantity: number) => {
    setQuantityInputs((prev) => {
      const next = { ...prev };
      delete next[lineId];
      return next;
    });

    if (newQuantity < 1) {
      // Decrementing past 1 is a removal — route through the same confirm UI as
      // the trash icon so the customer never deletes a line in one accidental click.
      setConfirmingRemoveId(lineId);
      return;
    }

    setUpdatingItems(prev => new Set(prev).add(lineId));
    await updateItem(lineId, newQuantity);
    setUpdatingItems(prev => {
      const next = new Set(prev);
      next.delete(lineId);
      return next;
    });
  };

  const handleQuantityInputCommit = (item: CartItem) => {
    const rawValue = quantityInputs[item.id];
    if (rawValue === undefined) return;

    const trimmedValue = rawValue.trim();
    if (trimmedValue === '') {
      setQuantityInputs((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }

    const newQuantity = Number.parseInt(trimmedValue, 10);
    if (!Number.isFinite(newQuantity) || newQuantity === item.quantity) {
      setQuantityInputs((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }

    void handleUpdateQuantity(item.id, newQuantity);
  };

  const handleRemoveItem = async (lineId: string) => {
    setUpdatingItems(prev => new Set(prev).add(lineId));
    await removeItem(lineId);
    setUpdatingItems(prev => {
      const next = new Set(prev);
      next.delete(lineId);
      return next;
    });
  };

  // Extract variant options nicely
  const formatVariantOptions = (options: Array<{ name: string; value: string }>) => {
    return options.map(opt => ({
      name: opt.name,
      value: opt.value
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              right: 0,
              top: 0,
              zIndex: 50,
              height: '100%',
              width: '100%',
              maxWidth: '420px',
              backgroundColor: '#ffffff',
              boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  margin: 0
                }}
              >
                Your Bag ({totalQuantity})
              </h2>
              <button
                onClick={closeCart}
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                aria-label="Close cart"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  style={{ width: '24px', height: '24px' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Cart Items */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: cartItems.length > 0 ? '0' : '24px'
              }}
            >
              {cartItems.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    textAlign: 'center',
                    padding: '48px 24px'
                  }}
                >
                  {/* Shopping Bag Icon */}
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px'
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1}
                      stroke="#9ca3af"
                      style={{ width: '40px', height: '40px' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                      />
                    </svg>
                  </div>

                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '8px'
                    }}
                  >
                    Your Bag is Empty
                  </h3>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '24px',
                      maxWidth: '280px'
                    }}
                  >
                    Looks like you haven&apos;t added anything to your bag yet.
                  </p>
                  <button
                    onClick={closeCart}
                    style={{
                      padding: '14px 40px',
                      backgroundColor: 'var(--brand-blue)',
                      color: '#ffffff',
                      fontSize: '13px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--brand-blue-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--brand-blue)'}
                  >
                    Start Shopping
                  </button>
                </div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  <AnimatePresence initial={false}>
                    {topLevelLines.flatMap((item) => {
                      const isUpdating = updatingItems.has(item.id);
                      const linkedFees = feeChildrenByGarmentId.get(item.id);
                      const hasFinishAttribute = item.attributes?.some(
                        (attribute) => attribute.key.toLowerCase() === 'finish' && attribute.value
                      );
                      const variantOptions = formatVariantOptions(item.merchandise.selectedOptions).filter(
                        (option) => !hasFinishAttribute || !['finish', 'variant'].includes(option.name.toLowerCase())
                      );
                      const isDigitisingFee = item.merchandise.product.handle.startsWith('embroidery-digitising-fee');
                      const linkedGarment = isDigitisingFee ? findLinkedGarment(item) : undefined;
                      const isLocked = isDigitisingFee && !!linkedGarment;
                      // Prefer the colour-matched image pinned at add-to-cart time
                      // (`_image`); falls back to the product's featured image,
                      // which is the primary variant (typically Black) and would
                      // otherwise mis-represent any non-primary colour selection.
                      const pinnedImageUrl = item.attributes?.find((a) => a.key === '_image')?.value;
                      const pinnedImageAlt = item.attributes?.find((a) => a.key === '_imageAlt')?.value;
                      const lineImageUrl = pinnedImageUrl || item.merchandise.product.featuredImage?.url;
                      const lineImageAlt = pinnedImageAlt || item.merchandise.product.featuredImage?.altText || item.merchandise.product.title;

                      const garmentRow = (
                        <motion.li
                          key={item.id}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            opacity: isUpdating ? 0.6 : 1,
                            transition: 'opacity 0.2s ease'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: '16px',
                              padding: '20px 24px'
                            }}
                          >
                            {/* Product Image — link to PDP for garments, plain box for fee lines */}
                            {(() => {
                              const imageBoxStyle = {
                                flexShrink: 0,
                                width: '100px',
                                height: '120px',
                                position: 'relative' as const,
                                backgroundColor: '#f5f5f5',
                                display: 'block' as const,
                              };
                              const imageInner = lineImageUrl ? (
                                <Image
                                  src={lineImageUrl}
                                  alt={lineImageAlt}
                                  fill
                                  style={{ objectFit: 'cover' }}
                                  sizes="100px"
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1}
                                    stroke="#d1d5db"
                                    style={{ width: '32px', height: '32px' }}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                    />
                                  </svg>
                                </div>
                              );
                              return isDigitisingFee ? (
                                <div style={imageBoxStyle}>{imageInner}</div>
                              ) : (
                                <Link
                                  href={`/products/${item.merchandise.product.handle}`}
                                  onClick={closeCart}
                                  style={imageBoxStyle}
                                >
                                  {imageInner}
                                </Link>
                              );
                            })()}

                            {/* Product Details */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Title — link to PDP for garments, plain text for fee lines */}
                              {(() => {
                                const titleStyle = {
                                  display: 'block' as const,
                                  fontSize: '14px',
                                  fontWeight: '600' as const,
                                  color: '#000000',
                                  textDecoration: 'none',
                                  marginBottom: '6px',
                                  overflow: 'hidden' as const,
                                  textOverflow: 'ellipsis' as const,
                                  whiteSpace: 'nowrap' as const,
                                };
                                return isDigitisingFee ? (
                                  <span style={titleStyle}>{item.merchandise.product.title}</span>
                                ) : (
                                  <Link
                                    href={`/products/${item.merchandise.product.handle}`}
                                    onClick={closeCart}
                                    style={titleStyle}
                                  >
                                    {item.merchandise.product.title}
                                  </Link>
                                );
                              })()}

                              {/* One-time fee label for digitising */}
                              {isDigitisingFee && (
                                <p style={{ fontSize: '11px', color: '#059669', fontWeight: '600', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  One-time setup fee
                                </p>
                              )}

                              {/* "Required for X" notice — fee is locked while linked garment is in cart */}
                              {isLocked && linkedGarment && (
                                <p style={{ fontSize: '11px', color: '#1f2937', backgroundColor: '#f3f4f6', padding: '6px 8px', borderRadius: '4px', margin: '0 0 6px 0', lineHeight: '1.4' }}>
                                  Required for <strong>{linkedGarment.merchandise.product.title}</strong>. Remove that item to remove this fee.
                                </p>
                              )}

                              {/* Awareness notice when fee qty has accumulated (same product added more than once) */}
                              {isDigitisingFee && item.quantity >= 2 && (
                                <p style={{ fontSize: '11px', color: '#92400e', backgroundColor: '#fef3c7', padding: '6px 8px', borderRadius: '4px', margin: '0 0 6px 0', lineHeight: '1.4' }}>
                                  Reusing the same design? Reduce this to 1. Keep it at {item.quantity} if each copy uses a different design.
                                </p>
                              )}

                              {/* Variant Options */}
                              <div style={{ marginBottom: '8px' }}>
                                {variantOptions.map((opt, index) => (
                                  <p
                                    key={index}
                                    style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                      margin: '2px 0',
                                      overflowWrap: 'anywhere'
                                    }}
                                  >
                                    {opt.name}: <span style={{ color: '#374151' }}>{opt.value}</span>
                                  </p>
                                ))}
                                {item.attributes?.filter(attr => attr.value && !attr.key.startsWith('_')).map((attr, index) => (
                                  <p
                                    key={`attr-${index}`}
                                    style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                      margin: '2px 0'
                                    }}
                                  >
                                    {attr.key.toLowerCase().startsWith('artwork') && /^https?:\/\//.test(attr.value) ? (
                                      <>{attr.key}: <a href={attr.value} target="_blank" rel="noopener noreferrer" style={{ color: '#374151', textDecoration: 'underline' }}>View uploaded file</a></>
                                    ) : (
                                      <>{attr.key}: <span style={{ color: '#374151' }}>{attr.value}</span></>
                                    )}
                                  </p>
                                ))}
                              </div>

                              {/* Price — unit price with a "× N" multiplier
                                  when qty > 1. Fees render as their own
                                  sibling rows below this garment, so no
                                  inline "+ £N.NN" addendum here. */}
                              <p
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  marginBottom: '12px',
                                }}
                              >
                                {formatPrice(item.merchandise.price)}
                                {item.quantity > 1 && (
                                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', marginLeft: '4px' }}>
                                    × {item.quantity}
                                  </span>
                                )}
                              </p>

                              {/* Quantity and Remove */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                {/* Quantity Selector */}
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <button
                                    onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                    disabled={isLoading || isUpdating || item.quantity <= 1}
                                    title={item.quantity <= 1 ? (isLocked ? 'Required for the embroidered design — remove the embroidered item to remove this fee.' : 'Use the trash icon to remove this item.') : undefined}
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#ffffff',
                                      border: 'none',
                                      cursor: isLoading || isUpdating || item.quantity <= 1 ? 'not-allowed' : 'pointer',
                                      fontSize: '16px',
                                      color: item.quantity <= 1 ? '#9ca3af' : '#374151',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isLoading && !isUpdating && item.quantity > 1) {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#ffffff';
                                    }}
                                    aria-label="Decrease quantity"
                                  >
                                    −
                                  </button>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={quantityInputs[item.id] ?? String(item.quantity)}
                                    onChange={(event) => {
                                      const value = event.target.value;
                                      if (/^\d*$/.test(value)) {
                                        setQuantityInputs((prev) => ({
                                          ...prev,
                                          [item.id]: value,
                                        }));
                                      }
                                    }}
                                    onBlur={() => handleQuantityInputCommit(item)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.currentTarget.blur();
                                      }
                                      if (event.key === 'Escape') {
                                        setQuantityInputs((prev) => {
                                          const next = { ...prev };
                                          delete next[item.id];
                                          return next;
                                        });
                                        event.currentTarget.blur();
                                      }
                                    }}
                                    disabled={isLoading || isUpdating}
                                    aria-label="Item quantity"
                                    style={{
                                      width: '40px',
                                      height: '32px',
                                      textAlign: 'center',
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      borderLeft: '1px solid #e5e7eb',
                                      borderRight: '1px solid #e5e7eb',
                                      borderTop: 'none',
                                      borderBottom: 'none',
                                      padding: '6px 0',
                                      backgroundColor: isLoading || isUpdating ? '#f9fafb' : '#ffffff',
                                      color: '#111827',
                                      outline: 'none',
                                      appearance: 'textfield',
                                    }}
                                  />
                                  <button
                                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                    disabled={isLoading || isUpdating}
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#ffffff',
                                      border: 'none',
                                      cursor: isLoading || isUpdating ? 'not-allowed' : 'pointer',
                                      fontSize: '16px',
                                      color: '#374151',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isLoading && !isUpdating) {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#ffffff';
                                    }}
                                    aria-label="Increase quantity"
                                  >
                                    +
                                  </button>
                                </div>

                                {/* Remove Button (Trash Icon) — opens confirm dialog. Locked
                                    fee lines render a "Required" lock badge instead, so the
                                    fee can't be removed while its embroidered garment is in cart. */}
                                {isLocked ? (
                                  <span
                                    title="Required for the embroidered design. Remove the embroidered item to remove this fee."
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '6px 10px',
                                      borderRadius: '4px',
                                      backgroundColor: '#f3f4f6',
                                      color: '#6b7280',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      cursor: 'help',
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.8}
                                      stroke="currentColor"
                                      style={{ width: '12px', height: '12px' }}
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                    Required
                                  </span>
                                ) : (
                                <button
                                  onClick={() => setConfirmingRemoveId(item.id)}
                                  disabled={isLoading || isUpdating}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: isLoading || isUpdating ? 'not-allowed' : 'pointer',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isLoading && !isUpdating) {
                                      e.currentTarget.style.backgroundColor = '#fef2f2';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                  aria-label="Remove item"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="#ef4444"
                                    style={{ width: '18px', height: '18px' }}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                    />
                                  </svg>
                                </button>
                                )}
                              </div>
                            </div>
                          </div>

                        </motion.li>
                      );

                      // Sibling fee rows — render each linked digitising fee
                      // as its own row directly below the garment, mirroring
                      // the visual treatment of Shopify's checkout order
                      // summary. The pairing/locking/cascade logic above is
                      // unchanged; this just consumes feeChildrenByGarmentId
                      // as siblings rather than as nested expandable detail.
                      const feeRows = (linkedFees ?? []).map(({ fee: feeLine, isPrimary }) => {
                        const side = feeSideOf(feeLine);
                        const shortLabel = side === 'front'
                          ? 'Embroidered Front'
                          : side === 'back'
                            ? 'Embroidered Back'
                            : feeLine.merchandise.product.title;
                        const feeImageUrl = feeLine.merchandise.product.featuredImage?.url;
                        const feeImageAlt = feeLine.merchandise.product.featuredImage?.altText || feeLine.merchandise.product.title;
                        const visibleFeeAttributes =
                          feeLine.attributes?.filter(
                            (a) => a.value && !a.key.startsWith('_') && a.key !== 'For'
                          ) ?? [];
                        return (
                          <motion.li
                            key={`fee-${feeLine.id}-under-${item.id}`}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ borderBottom: '1px solid #e5e7eb' }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                gap: '16px',
                                padding: '20px 24px',
                              }}
                            >
                              <div
                                style={{
                                  flexShrink: 0,
                                  width: '100px',
                                  height: '120px',
                                  position: 'relative',
                                  backgroundColor: '#f5f5f5',
                                }}
                              >
                                {feeImageUrl ? (
                                  <Image
                                    src={feeImageUrl}
                                    alt={feeImageAlt}
                                    fill
                                    style={{ objectFit: 'cover' }}
                                    sizes="100px"
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1}
                                      stroke="#d1d5db"
                                      style={{ width: '32px', height: '32px' }}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span
                                  style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#000000',
                                    marginBottom: '6px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {shortLabel}
                                </span>

                                <p style={{ fontSize: '11px', color: '#059669', fontWeight: 600, margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  One-time setup fee
                                </p>

                                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px 0', lineHeight: '1.4' }}>
                                  For: <span style={{ color: '#374151' }}>{item.merchandise.product.title}</span>
                                </p>

                                {visibleFeeAttributes.length > 0 && (
                                  <div style={{ marginBottom: '8px' }}>
                                    {visibleFeeAttributes.map((attr, i) => (
                                      <p key={`fee-attr-${i}`} style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0', overflowWrap: 'anywhere' }}>
                                        {attr.key.toLowerCase().startsWith('artwork') && /^https?:\/\//.test(attr.value) ? (
                                          <>{attr.key}: <a href={attr.value} target="_blank" rel="noopener noreferrer" style={{ color: '#374151', textDecoration: 'underline' }}>View uploaded file</a></>
                                        ) : (
                                          <>{attr.key}: <span style={{ color: '#374151' }}>{attr.value}</span></>
                                        )}
                                      </p>
                                    ))}
                                  </div>
                                )}

                                {isPrimary ? (
                                  <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
                                    {formatPrice(feeLine.merchandise.price)}
                                    <span style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280', marginLeft: '6px' }}>
                                      (one-time)
                                    </span>
                                  </p>
                                ) : (
                                  <p
                                    title="One-time embroidery setup fee — already added with another size in your cart, so nothing extra here."
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      margin: '0 0 12px 0',
                                      fontSize: '12px',
                                      color: '#059669',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth={2.2}
                                      style={{ width: '12px', height: '12px', flexShrink: 0 }}
                                      aria-hidden="true"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                    </svg>
                                    <span>Included (one-time)</span>
                                  </p>
                                )}

                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  <span
                                    title="Required for the embroidered design. Remove the embroidered item to remove this fee."
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '6px 10px',
                                      borderRadius: '4px',
                                      backgroundColor: '#f3f4f6',
                                      color: '#6b7280',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      cursor: 'help',
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.8}
                                      stroke="currentColor"
                                      style={{ width: '12px', height: '12px' }}
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                    Required
                                  </span>
                                </div>
                              </div>
                            </div>
                          </motion.li>
                        );
                      });

                      return [garmentRow, ...feeRows];
                    })}
                  </AnimatePresence>
                </ul>
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div
                style={{
                  padding: '20px 24px',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff'
                }}
              >
                {/* Subtotal */}
                {discountLadderEnabled && (
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                      padding: '14px',
                      marginBottom: '14px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '12px',
                        marginBottom: '10px',
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '0.7px',
                            textTransform: 'uppercase',
                            color: '#111827',
                          }}
                        >
                          Bulk Discount Ladder
                        </p>
                        <p
                          style={{
                            margin: '4px 0 0',
                            fontSize: '12px',
                            color: '#6b7280',
                            lineHeight: 1.45,
                          }}
                        >
                          {eligibleTier
                            ? `You've unlocked ${eligibleTier.percent}% off — estimated saving ${formatCurrency(estimatedSavings, subtotalCurrency)}.`
                            : `Spend ${formatCurrency(amountToNextTier, subtotalCurrency)} more to unlock 5% off.`}
                        </p>
                      </div>
                      {eligibleTier && (
                        <span
                          style={{
                            flexShrink: 0,
                            border: '1px solid #111827',
                            padding: '4px 7px',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          {eligibleTier.code}
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        height: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${ladderProgress}%`,
                          backgroundColor: '#111827',
                          transition: 'width 0.25s ease',
                        }}
                      />
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '6px',
                        marginTop: '10px',
                      }}
                    >
                      {DISCOUNT_TIERS.map((tier) => {
                        const unlocked = subtotalAmount >= tier.threshold;
                        return (
                          <span
                            key={tier.code}
                            style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              color: unlocked ? '#111827' : '#9ca3af',
                              textTransform: 'uppercase',
                            }}
                          >
                            {tier.percent}% at {formatCurrency(tier.threshold, subtotalCurrency)}
                          </span>
                        );
                      })}
                    </div>

                    {visibleDiscountMessage && (
                      <p
                        style={{
                          margin: '10px 0 0',
                          fontSize: '11px',
                          color: visibleDiscountMessage.includes('could not') ||
                            visibleDiscountMessage.includes('already') ||
                            visibleDiscountMessage.includes('not active')
                            ? '#b45309'
                            : '#047857',
                        }}
                      >
                        {visibleDiscountMessage}
                      </p>
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Subtotal</span>
                  <span style={{ fontSize: '18px', fontWeight: '700' }}>
                    {subtotal ? formatPrice(subtotal) : '£0.00'}
                  </span>
                </div>

                {/* Shipping Note */}
                <p
                  style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    marginBottom: '16px'
                  }}
                >
                  Shipping & taxes calculated at checkout
                </p>

                {/* Checkout Button — sends straight to Shopify-hosted checkout on shop.oktshirt.co.uk */}
                <a
                  href={cart?.checkoutUrl || '#'}
                  onClick={closeCart}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    backgroundColor: 'var(--brand-blue)',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    textDecoration: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--brand-blue-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--brand-blue)'}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    style={{ width: '18px', height: '18px' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                  Checkout Securely
                </a>

                {/* Continue Shopping */}
                <button
                  onClick={closeCart}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    width: '100%',
                    padding: '14px',
                    marginTop: '12px',
                    backgroundColor: 'transparent',
                    color: '#374151',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#000000'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    style={{ width: '16px', height: '16px' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                    />
                  </svg>
                  Continue Shopping
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}

      {/* Remove-from-cart confirmation dialog */}
      {confirmingRemoveId && (() => {
        const target = cartItems.find((l) => l.id === confirmingRemoveId);
        if (!target) return null;
        const isFee = target.merchandise.product.handle.startsWith('embroidery-digitising-fee');
        return (
          <>
            {/* Dialog backdrop — confined to the drawer area only */}
            <motion.div
              key="confirm-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setConfirmingRemoveId(null)}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                height: '100%',
                width: '100%',
                maxWidth: '420px',
                zIndex: 60,
                backgroundColor: 'rgba(17, 24, 39, 0.45)',
              }}
            />
            {/* Dialog — centered inside the drawer, not the viewport */}
            <motion.div
              key="confirm-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-remove-title"
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.18 }}
              style={{
                position: 'fixed',
                top: '50%',
                right: '20px',
                transform: 'translateY(-50%)',
                zIndex: 61,
                width: 'calc(100% - 40px)',
                maxWidth: '380px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
                padding: '20px 22px',
              }}
            >
              <h3
                id="confirm-remove-title"
                style={{
                  margin: '0 0 8px 0',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#000000',
                  letterSpacing: '0.2px',
                }}
              >
                Remove from cart?
              </h3>
              <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#374151', lineHeight: '1.45' }}>
                <strong style={{ fontWeight: '600' }}>{target.merchandise.product.title}</strong>
                {!isFee && ' will be removed from your cart.'}
                {isFee && ' (digitising fee) will be removed.'}
              </p>
              {!isFee && (
                <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
                  Any digitising fee linked to this item will also be removed.
                </p>
              )}
              {isFee && <div style={{ height: '10px' }} />}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  onClick={() => setConfirmingRemoveId(null)}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '500',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const id = confirmingRemoveId;
                    setConfirmingRemoveId(null);
                    if (id) await handleRemoveItem(id);
                  }}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; }}
                >
                  Remove
                </button>
              </div>
            </motion.div>
          </>
        );
      })()}
    </AnimatePresence>
  );
}
